import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckSquare, Download, FileText, Printer, Square } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/project/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/project/lib/auth";
import { inr, shortDate } from "@/project/lib/format";
import {
  orgsQuery,
  settingsQuery,
  checklistQuery,
} from "@/project/lib/queries";
import { LetterSheet } from "@/project/components/LetterSheet";
import { printElementAsPdf } from "@/project/lib/print-doc";

export function LetterStep({
  applicationId,
  section = "all",
}: {
  applicationId: string;
  section?: "all" | "letters" | "history";
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const lettersRef = useRef<HTMLDivElement>(null);

  const downloadAllPdf = () => {
    const el = lettersRef.current;
    if (!el?.textContent?.trim()) {
      toast.error("Select at least one organization to download letters.");
      return;
    }
    printElementAsPdf("Recommendation Letters", el);
  };

  const { data: app } = useQuery({
    queryKey: ["application", applicationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("applications")
        .select(
          "*, assistance_categories(name), application_organizations(organization_id, requested_amount)"
        )
        .eq("id", applicationId)
        .single();
      if (error) {
        throw error;
      }
      return data;
    },
  });
  const { data: orgs } = useQuery(orgsQuery);
  const { data: settings } = useQuery(settingsQuery);
  const { data: checklist } = useQuery(checklistQuery);
  const { data: letters } = useQuery({
    queryKey: ["letters", applicationId],
    queryFn: async () => {
      const { data } = await supabase
        .from("generated_letters")
        .select("*")
        .eq("application_id", applicationId)
        .order("generated_at", { ascending: false });
      return data ?? [];
    },
  });

  useEffect(() => {
    if (!app) {
      return;
    }
    setSelected(
      (app.application_organizations ?? []).map(o => o.organization_id)
    );
    setAmounts(
      Object.fromEntries(
        (app.application_organizations ?? []).map(o => [
          o.organization_id,
          o.requested_amount ? String(o.requested_amount) : "",
        ])
      )
    );
  }, [app]);

  const activeOrgs = (orgs ?? []).filter(o => o.active);
  const selectedOrgs = useMemo(
    () => activeOrgs.filter(o => selected.includes(o.id)),
    [activeOrgs, selected]
  );
  const documents = useMemo(() => {
    const active = (checklist ?? []).filter(d => d.active);
    const scoped = active.filter(
      d => d.category_id === app?.category_id || d.category_id === null
    );
    return (scoped.length ? scoped : active).map(d => d.document_name);
  }, [checklist, app?.category_id]);

  const allocated = useMemo(
    () => selected.reduce((s, id) => s + Number(amounts[id] || 0), 0),
    [selected, amounts]
  );
  const balanceRequired = Number(app?.balance_required ?? 0);
  const unallocated = balanceRequired - allocated;

  const saveSelection = async (silent = false) => {
    await supabase
      .from("application_organizations")
      .delete()
      .eq("application_id", applicationId);
    if (selected.length) {
      await supabase.from("application_organizations").insert(
        selected.map(organization_id => ({
          application_id: applicationId,
          organization_id,
          requested_amount: Number(amounts[organization_id] || 0),
        }))
      );
    }
    if (!silent) {
      toast.success(
        `${selected.length} organization(s) saved for this application`
      );
    }
  };

  const generateLetters = async () => {
    if (!selected.length) {
      toast.error("Select at least one organization before generating letters");
      return;
    }
    await saveSelection(true);
    const days = Number(settings?.["letter_validity_days"] ?? 15);
    const validUntil = new Date(Date.now() + days * 86400000)
      .toISOString()
      .slice(0, 10);
    const { error } = await supabase.from("generated_letters").insert(
      selectedOrgs.map(o => ({
        application_id: applicationId,
        organization_id: o.id,
        organization_name: o.short_name ?? o.name,
        requested_amount: Number(amounts[o.id] || 0),
        letter_date:
          app?.application_date ?? new Date().toISOString().slice(0, 10),
        valid_until: validUntil,
        created_date:
          app?.created_at?.slice(0, 10) ??
          new Date().toISOString().slice(0, 10),
        generated_by: user?.id ?? null,
      }))
    );
    if (error) {
      toast.error(error.message);
      return;
    }
    await supabase
      .from("applications")
      .update({ status: "Letter Generated" })
      .eq("id", applicationId);
    qc.invalidateQueries({ queryKey: ["letters", applicationId] });
    qc.invalidateQueries({ queryKey: ["application", applicationId] });
    toast.success(`${selectedOrgs.length} letter(s) recorded in history`);
  };

  if (!app) {
    return <p className="text-muted-foreground">Loading application…</p>;
  }

  const letterMeta = new Map<
    string,
    { created?: string | null; generated?: string | null }
  >();
  for (const l of letters ?? []) {
    if (l.organization_id && !letterMeta.has(l.organization_id)) {
      letterMeta.set(l.organization_id, {
        created: (l as { created_date?: string | null }).created_date ?? null,
        generated: l.generated_at?.slice(0, 10) ?? null,
      });
    }
  }

  const letterApp = {
    ref_no: app.ref_no,
    application_date: app.application_date,
    applicant_name: app.applicant_name,
    applicant_address: app.applicant_address,
    applicant_mobile: app.applicant_mobile,
    applicant_aadhaar: app.applicant_aadhaar,
    ration_card_no: app.ration_card_no,
    beneficiary_name: app.beneficiary_name,
    total_requirement: Number(app.total_requirement),
    applicant_contribution: Number(app.applicant_contribution),
    our_contribution: Number(app.our_contribution),
    other_received: Number(app.other_received),
    balance_required: Number(app.balance_required),
    bank_account_holder: app.bank_account_holder,
    bank_name: app.bank_name,
    bank_account_no: app.bank_account_no,
    bank_ifsc: app.bank_ifsc,
    categoryName: app.assistance_categories?.name ?? "",
  };

  return (
    <div className="space-y-4">
      {section !== "history" && (
        <>
          <Card className="no-print">
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-base">
                Select Partner Organizations — {selected.length} selected
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelected(activeOrgs.map(o => o.id))}
                >
                  <CheckSquare className="mr-2 h-4 w-4" /> Select All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelected([])}
                >
                  <Square className="mr-2 h-4 w-4" /> Clear
                </Button>
              </div>
            </CardHeader>
            <CardContent className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {activeOrgs.map(o => (
                <label
                  key={o.id}
                  className="hover:bg-muted/50 flex items-start gap-3 rounded-md border border-border p-3 text-sm"
                >
                  <Checkbox
                    checked={selected.includes(o.id)}
                    onCheckedChange={c =>
                      setSelected(prev =>
                        c ? [...prev, o.id] : prev.filter(x => x !== o.id)
                      )
                    }
                  />
                  <span className="flex-1">
                    <span className="font-medium">
                      {o.short_name ?? o.name}
                    </span>
                    {selected.includes(o.id) && (
                      <span className="mt-2 block">
                        <span className="mb-1 block text-xs uppercase text-muted-foreground">
                          Amount requested
                        </span>
                        <Input
                          inputMode="numeric"
                          placeholder="e.g. 25000"
                          value={amounts[o.id] ?? ""}
                          onChange={e =>
                            setAmounts(prev => ({
                              ...prev,
                              [o.id]: e.target.value,
                            }))
                          }
                        />
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </CardContent>
            <div className="flex flex-wrap gap-4 border-t border-border px-6 py-3 text-sm">
              <span>
                Balance required: <strong>{inr(balanceRequired)}</strong>
              </span>
              <span>
                Allocated across {selected.length} institution(s):{" "}
                <strong>{inr(allocated)}</strong>
              </span>
              <span
                className={
                  unallocated > 0 ? "text-destructive" : "text-muted-foreground"
                }
              >
                {unallocated > 0
                  ? `Still to be covered: ${inr(unallocated)} — add another institution`
                  : unallocated < 0
                    ? `Over-allocated by ${inr(Math.abs(unallocated))}`
                    : "Fully covered"}
              </span>
            </div>
          </Card>

          <div className="no-print flex flex-wrap gap-3">
            <Button onClick={generateLetters}>
              <FileText className="mr-2 h-4 w-4" /> Generate Recommendation
              Letters
            </Button>
            <Button variant="secondary" onClick={() => window.print()}>
              <Printer className="mr-2 h-4 w-4" /> Print All Letters
            </Button>
            <Button variant="secondary" onClick={downloadAllPdf}>
              <Download className="mr-2 h-4 w-4" /> Download PDF (All Letters)
            </Button>
            <Button variant="outline" onClick={() => saveSelection()}>
              Save Selection
            </Button>
          </div>

          <div ref={lettersRef} className="space-y-6">
            {selectedOrgs.map((o, i) => (
              <div key={o.id}>
                <p className="no-print mb-1 text-xs text-muted-foreground">
                  Letter {i + 1} of {selectedOrgs.length} —{" "}
                  {o.short_name ?? o.name}
                </p>
                <LetterSheet
                  app={letterApp}
                  org={o}
                  allOrgs={activeOrgs}
                  amounts={amounts}
                  documents={documents}
                  settings={settings ?? {}}
                  createdDate={letterMeta.get(o.id)?.created ?? null}
                  generatedDate={letterMeta.get(o.id)?.generated ?? null}
                />
              </div>
            ))}
            {!selectedOrgs.length && (
              <p className="text-sm text-muted-foreground">
                Select organizations above to preview the letters.
              </p>
            )}
          </div>
        </>
      )}

      {section !== "letters" && (
        <Card className="no-print">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Letter History</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <table className="data-table w-full text-sm">
              <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Organization</th>
                  <th className="px-4 py-2">Amount</th>
                  <th className="px-4 py-2">Letter Date</th>
                  <th className="px-4 py-2">Valid Until</th>
                  <th className="px-4 py-2">Letter Created</th>
                  <th className="px-4 py-2">Letter Generated</th>
                  <th className="px-4 py-2">Generated At</th>
                </tr>
              </thead>
              <tbody>
                {(letters ?? []).map(l => (
                  <tr key={l.id} className="border-t border-border">
                    <td className="px-4 py-2">{l.organization_name}</td>
                    <td className="px-4 py-2">{inr(l.requested_amount)}</td>
                    <td className="px-4 py-2">{shortDate(l.letter_date)}</td>
                    <td className="px-4 py-2">{shortDate(l.valid_until)}</td>
                    <td className="px-4 py-2">{shortDate(l.created_date)}</td>
                    <td className="px-4 py-2">
                      {shortDate(l.generated_at?.slice(0, 10))}
                    </td>
                    <td className="px-4 py-2">
                      {new Date(l.generated_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
                {!letters?.length && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-6 text-center text-muted-foreground"
                    >
                      No letters generated yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
